import type { useTrans } from '@libs/i18n';
import { z, type ZodTypeAny } from 'zod';

import { PHONE_REGEX } from '@libs/constants';

import type {
  ExtractionReviewField,
  ProfileFieldValidation,
} from '../../api/types';
import {
  asTableRows,
  coerceScalarForSave,
  normalizeTableCells,
} from '../../organization-profile/utils/fieldValue';

/**
 * Builds a react-hook-form Zod schema (and matching default values) for the
 * extraction-review screen, derived from each field's type + validation rules.
 *
 * Mirrors the Organization Profile's `buildCategorySchema` (empty values pass —
 * saving is partial, so `required`/`minItems` are never enforced), with two
 * review-specific additions the org form doesn't need:
 *  - `table` cells are validated against their column rules (e.g. a column's
 *    `maxLength`) via a `superRefine`; the org schema treats a table as opaque.
 *  - Fully-empty table rows are skipped (they're stripped before saving), so a
 *    blank row never trips its column's checks.
 */

type Translate = ReturnType<typeof useTrans>['t'];

const LIST_TYPES = new Set(['multi_select', 'string_list', 'number_list', 'list']);

function safeRegex(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
}

/** A row counts as empty when every cell is null, '', or an empty array. */
function isEmptyRow(row: Record<string, unknown>): boolean {
  return Object.values(row).every(
    (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0),
  );
}

/**
 * Zod schema for a SCALAR value (a whole scalar field, or a single table cell).
 * `select` membership and `multi_select` entries are sanitized away before save
 * (out-of-option values are dropped), so only format/length/range rules matter.
 */
function scalarSchema(
  type: string,
  validation: ProfileFieldValidation | null,
  t: Translate,
): ZodTypeAny {
  const v: ProfileFieldValidation = validation ?? {};

  switch (type) {
    case 'string':
    case 'textarea':
    case 'select':
    case 'date': {
      let s = z.string();
      if (v.minLength != null)
        s = s.min(
          v.minLength,
          t('app.documentHub.review.validation.minLength', { min: v.minLength }),
        );
      if (v.maxLength != null)
        s = s.max(
          v.maxLength,
          t('app.documentHub.review.validation.maxLength', { max: v.maxLength }),
        );
      if (v.pattern) {
        const re = safeRegex(v.pattern);
        if (re) s = s.regex(re, t('app.documentHub.review.validation.pattern'));
      }
      return s;
    }
    case 'email': {
      if (v.pattern) {
        const re = safeRegex(v.pattern);
        if (re) return z.string().regex(re, t('app.documentHub.review.validation.pattern'));
      }
      return z.string().email(t('app.documentHub.review.validation.email'));
    }
    case 'phone': {
      if (v.pattern) {
        const re = safeRegex(v.pattern);
        if (re) return z.string().regex(re, t('app.documentHub.review.validation.pattern'));
      }
      return z
        .string()
        .regex(PHONE_REGEX, t('app.documentHub.review.validation.phone'));
    }
    case 'number': {
      let n = z.number({
        error: t('app.documentHub.review.validation.number'),
      });
      if (v.integerOnly)
        n = n.int(t('app.documentHub.review.validation.integer'));
      if (v.min != null)
        n = n.min(v.min, t('app.documentHub.review.validation.min', { min: v.min }));
      if (v.max != null)
        n = n.max(v.max, t('app.documentHub.review.validation.max', { max: v.max }));
      return n;
    }
    default:
      return z.unknown();
  }
}

/** Schema for a `table` field: bounds rows and validates each non-empty cell. */
function tableSchema(field: ExtractionReviewField, t: Translate): ZodTypeAny {
  const v: ProfileFieldValidation =
    (field.validation as ProfileFieldValidation | null) ?? {};
  const columns = field.columns ?? [];

  return z
    .array(z.record(z.string(), z.unknown()))
    .superRefine((rows, ctx) => {
      // `minItems` is intentionally not enforced (partial saves). Count only the
      // rows that survive the empty-row strip applied at save time.
      const kept = rows.filter(
        (r) => r && typeof r === 'object' && !Array.isArray(r) && !isEmptyRow(r),
      );
      if (v.maxItems != null && kept.length > v.maxItems) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('app.documentHub.review.validation.maxItems', {
            max: v.maxItems,
          }),
        });
      }

      rows.forEach((row, i) => {
        if (typeof row !== 'object' || row === null || Array.isArray(row)) return;
        const r = row as Record<string, unknown>;
        // Empty rows are dropped before saving — never flag them.
        if (isEmptyRow(r)) return;
        for (const column of columns) {
          const cell = r[column.key];
          const columnLabel = column.label ?? column.key;
          const absent = cell === undefined || cell === null || cell === '';
          if (absent) {
            // A column may be required (authored in the catalog).
            if (column.isRequired)
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('app.documentHub.review.validation.cell', {
                  row: i + 1,
                  column: columnLabel,
                  message: t('app.documentHub.review.validation.requiredCell'),
                }),
              });
            continue;
          }
          const result = scalarSchema(
            column.type,
            column.validation ?? null,
            t,
          ).safeParse(cell);
          if (!result.success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('app.documentHub.review.validation.cell', {
                row: i + 1,
                column: columnLabel,
                message:
                  result.error.issues[0]?.message ??
                  t('app.documentHub.review.validation.pattern'),
              }),
            });
          }
        }
      });
    });
}

/** Schema for a `reference` composite value (`{ text, source }`). */
function referenceSchema(
  field: ExtractionReviewField,
  v: ProfileFieldValidation,
  t: Translate,
): ZodTypeAny {
  const source = z
    .object({ url: z.string().url().optional(), note: z.string().optional() })
    .nullable();
  const schema = z.object({
    text: scalarSchema('string', v, t),
    source: source.optional(),
  });
  if (v.sourceRequired) {
    return schema.refine(
      (val) => !!(val.source && (val.source.url || val.source.note)),
      {
        message: t('app.documentHub.review.validation.sourceRequired'),
        path: ['source'],
      },
    );
  }
  return schema;
}

/** The "filled value" schema for a field, before empty→null + nullability. */
function baseSchema(field: ExtractionReviewField, t: Translate): ZodTypeAny {
  const v: ProfileFieldValidation =
    (field.validation as ProfileFieldValidation | null) ?? {};

  if (field.type === 'table') return tableSchema(field, t);
  if (field.type === 'file') return z.array(z.unknown());
  if (field.type === 'reference') return referenceSchema(field, v, t);
  // `boolean`/`date` are validated strictly only at the top level (not inside a
  // table cell, where the shared `scalarSchema` stays lenient and the save-time
  // `normalizeTableCells` fixes shapes). AI values are coerced in
  // `buildReviewDefaults`, so a genuine boolean/date passes; only an
  // unrecoverable value (an unparseable date) surfaces here as a visible error
  // rather than a silent backend 422.
  if (field.type === 'boolean') return z.boolean();
  if (field.type === 'date') {
    let s = z.string();
    if (v.pattern) {
      const re = safeRegex(v.pattern);
      if (re) s = s.regex(re, t('app.documentHub.review.validation.pattern'));
    }
    return s.refine(
      (val) => !Number.isNaN(Date.parse(val)),
      t('app.documentHub.review.validation.pattern'),
    );
  }
  if (field.type === 'number_list') {
    let a = z.array(z.number());
    if (v.maxItems != null)
      a = a.max(
        v.maxItems,
        t('app.documentHub.review.validation.maxItems', { max: v.maxItems }),
      );
    return a;
  }
  if (LIST_TYPES.has(field.type)) {
    let a = z.array(z.string());
    if (v.maxItems != null)
      a = a.max(
        v.maxItems,
        t('app.documentHub.review.validation.maxItems', { max: v.maxItems }),
      );
    return a;
  }
  return scalarSchema(field.type, v, t);
}

function fieldSchema(field: ExtractionReviewField, t: Translate): ZodTypeAny {
  // Empty text arrives as '' — treat it as "not provided" (null) so length/
  // format rules don't reject a field the user simply left blank. No `required`
  // enforcement: a partial review can always be saved.
  return z.preprocess(
    (val) => (val === '' ? null : val),
    baseSchema(field, t).nullable(),
  );
}

export type ReviewFormValues = Record<string, unknown>;

export function buildReviewSchema(
  fields: ExtractionReviewField[],
  t: Translate,
): z.ZodTypeAny {
  const shape: Record<string, ZodTypeAny> = {};
  for (const f of fields) shape[f.fieldId] = fieldSchema(f, t);
  return z.object(shape);
}

/**
 * Coerce a field's raw AI value into the typed shape the backend expects, so the
 * form (and its Zod gate) work with the same value the save will send — a
 * list column that arrived as a comma string becomes an array, a `"true"`
 * boolean a real boolean, etc. Keeps form validation and the persisted payload
 * consistent so an AI-shaped value can't pass one and fail the other.
 */
function normalizeAiValue(field: ExtractionReviewField): unknown {
  const raw = field.aiValue;
  if (raw == null) return null;
  if (field.type === 'table') {
    const rows = asTableRows(raw);
    const columns = field.columns ?? [];
    return columns.length > 0 ? normalizeTableCells(rows, columns) : rows;
  }
  if (field.type === 'file' || field.type === 'reference') return raw;
  return coerceScalarForSave(field.type, raw, field.options ?? null);
}

export function buildReviewDefaults(
  fields: ExtractionReviewField[],
): ReviewFormValues {
  const defaults: ReviewFormValues = {};
  for (const f of fields) {
    // `file` (and table/list) default to an empty array; `reference` is a
    // composite object and defaults to null like a scalar.
    const listLike =
      f.type === 'table' || f.type === 'file' || LIST_TYPES.has(f.type);
    defaults[f.fieldId] = normalizeAiValue(f) ?? (listLike ? [] : null);
  }
  return defaults;
}
